/**
 * Dependency imports
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, BoxProps, Group, Stack, Text, Title } from '@mantine/core';
import Button from '../Button';
import FAIcon from '../FAIcon';

/**
 * Local imports
 */

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<PricingPlanCombinedProps>>(
  (props) => css`
    border: 1px solid rgba(0, 0, 0, 0.1);
  `
);

/**
 * PricingPlan Component
 */
export interface PricingPlanProps {
  name: string;
  description?: React.ReactNode;
  callToAction?: React.ReactNode;
  price?: number;
  cycle?: 'yearly' | 'monthly' | 'none' | string;
  features?: React.ReactNode[];
  variant?: 'default' | 'inverted' | 'featured';
}

export type PricingPlanCombinedProps = PricingPlanProps & BoxProps;

const PricingPlan: React.FC<PricingPlanCombinedProps> = ({
  name,
  description = '',
  callToAction = 'Get Started',
  price = 0,
  cycle = 'yearly',
  features = [],
  variant = 'default',
  ...props
}) => {
  return (
    <Root {...props}>
      <Box
        component="header"
        p="1.5rem"
        c={variant === 'inverted' ? 'white' : 'inherit'}
        bg={variant === 'inverted' ? 'dark.8' : 'rgba(0, 0, 0, 0.025)'}
      >
        <Title size={24} fw={500}>
          {name}
        </Title>

        <Text
          c={variant === 'inverted' ? 'dark.1' : 'dark.3'}
          fz={14}
          fw={400}
          mt={10}
        >
          {description}
        </Text>
      </Box>

      <Box px="1.5rem" pb="1.5rem">
        <Group gap={7}>
          <Title size={42} fw={900} my={24}>
            {price === 0 ? 'Free' : `$${price}`}
          </Title>

          {cycle !== 'none' && price > 0 && (
            <Stack gap={0}>
              <Text fz={12} lh={1.25}>
                CAD
              </Text>
              <Text fz={12} lh={1.25}>
                /
                {{
                  yearly: 'year',
                  monthly: 'month',
                }[cycle] || cycle}
              </Text>
            </Stack>
          )}
        </Group>

        <Box my={10}>
          {typeof callToAction === 'string' ? (
            <Button
              variant={variant === 'inverted' ? 'filled' : 'outline'}
              color="dark"
              fullwidth
            >
              {callToAction}
            </Button>
          ) : (
            callToAction
          )}
        </Box>

        <Box mt={60}>
          <Text>
            <strong>{name}</strong> includes:
          </Text>

          <Stack mt={10} gap={5}>
            {features.map((feature, index) => (
              <Group key={index} align="start">
                <Box>
                  <FAIcon name="check" color="primary" />
                </Box>
                {feature}
              </Group>
            ))}
          </Stack>
        </Box>
      </Box>
    </Root>
  );
};

export default PricingPlan;
